import React, { useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, IconButton, Popover, Tooltip, Typography } from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// feat_395 — historial de cambios de estado de un contacto
// (GET /contacts/:id/state-history, feat_394). Se pide recién al abrir.

const PAGE = 20;

const useStyles = makeStyles(theme => ({
  button: { padding: theme.spacing(0.5) },
  icon: { fontSize: "1rem" },
  paper: { width: theme.spacing(42), maxHeight: theme.spacing(55), padding: theme.spacing(1.5) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  row: {
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  transition: { display: "flex", alignItems: "center", gap: theme.spacing(0.5), flexWrap: "wrap" },
  dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block", marginRight: 3 },
  meta: { fontSize: "0.75em", color: theme.palette.text.secondary },
  reason: { fontSize: "0.8em", fontStyle: "italic" },
}));

const StateName = ({ state, classes }) => (
  <span>
    <span className={classes.dot} style={{ background: state?.color || "#9e9e9e" }} />
    {state?.name || i18n.t("contactStateModal.noState")}
  </span>
);

const ContactStateHistory = ({ contactId }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(
    async offset => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await api.get(`/contacts/${contactId}/state-history`, { params: { limit: PAGE, offset } });
        setItems(prev => (offset === 0 ? data.history || [] : [...prev, ...(data.history || [])]));
        setHasMore(!!data.hasMore);
      } catch (_) {
        setError(true);
      }
      setLoading(false);
    },
    [contactId]
  );

  if (!contactId) return null;

  const sourceLabel = s =>
    s === "api" ? i18n.t("crmBoard.historyApi") : s === "board" ? i18n.t("crmBoard.historyBoard") : "";

  return (
    <>
      <Tooltip title={i18n.t("crmBoard.historyTitle")} arrow>
        <IconButton
          size="small"
          className={classes.button}
          aria-label={i18n.t("crmBoard.historyTitle")}
          onClick={e => {
            setAnchor(e.currentTarget);
            load(0);
          }}
        >
          <HistoryIcon className={classes.icon} />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.paper }}
      >
        <Typography variant="subtitle2" className={classes.title}>
          {i18n.t("crmBoard.historyTitle")}
        </Typography>
        {error && <Typography variant="body2">{i18n.t("crmBoard.historyLoadError")}</Typography>}
        {!error && !loading && items.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("crmBoard.historyEmpty")}
          </Typography>
        )}
        {items.map(h => {
          const src = sourceLabel(h.source);
          return (
            <div key={h.id} className={classes.row}>
              <div className={classes.transition}>
                <StateName state={h.fromState} classes={classes} />
                <span>→</span>
                <StateName state={h.toState} classes={classes} />
              </div>
              {(h.lostReason?.name || h.lostReasonText) && (
                <div className={classes.reason}>
                  {[h.lostReason?.name, h.lostReasonText].filter(Boolean).join(" — ")}
                </div>
              )}
              <div className={classes.meta}>
                {new Date(h.createdAt).toLocaleString()} · {h.user?.name || i18n.t("crmBoard.historyUnknownUser")}
                {src ? ` · ${src}` : ""}
              </div>
            </div>
          );
        })}
        {loading && <CircularProgress size={18} />}
        {hasMore && !loading && (
          <Button size="small" onClick={() => load(items.length)}>
            {i18n.t("crmBoard.historyMore")}
          </Button>
        )}
      </Popover>
    </>
  );
};

export default ContactStateHistory;
